class PiltronBundleAddons extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });

    const section = this.closest('.shopify-section');
    section?.addEventListener('shopify:product:select', this.#handleProductSelect, { signal });

    this.#showPanel(this.dataset.selectedVariantId || '');
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  getSelectedItems() {
    const activePanel = this.querySelector('[data-addon-panel]:not([hidden])');
    if (!activePanel) return [];

    return Array.from(activePanel.querySelectorAll('[data-addon-item]'))
      .map((item) => {
        const input = item.querySelector('[data-addon-quantity]');
        const quantity = Number(input?.value) || 0;
        return {
          variantId: item.dataset.addonVariantId || '',
          title: item.dataset.addonTitle || '',
          quantity,
        };
      })
      .filter((item) => item.variantId && item.quantity > 0);
  }

  refreshPanel(variantId) {
    this.dataset.selectedVariantId = String(variantId || '');
    this.#showPanel(this.dataset.selectedVariantId);
  }

  reset() {
    for (const input of this.querySelectorAll('[data-addon-quantity]')) {
      input.value = '0';
      this.#syncQuantityControls(input);
    }
    this.#setStatus('');
  }

  #handleClick = (event) => {
    const button = event.target.closest('[data-addon-increase], [data-addon-decrease]');
    if (!button || !this.contains(button)) return;

    const item = button.closest('[data-addon-item]');
    const input = item?.querySelector('[data-addon-quantity]');
    if (!input || input.disabled) return;

    const change = button.hasAttribute('data-addon-increase') ? 1 : -1;
    this.#setQuantity(input, Number(input.value) + change);
  };

  #handleInput = (event) => {
    const input = event.target.closest('[data-addon-quantity]');
    if (!input || !this.contains(input)) return;
    this.#setQuantity(input, Number(input.value));
  };

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.reset();
    this.setAttribute('aria-busy', 'true');
    this.#setStatus('جاري تحديث الإضافات المتاحة للنسخة المختارة…');

    event.promise
      ?.then(({ detail }) => {
        const variantId = detail?.resource?.id;
        if (!variantId) {
          this.#showPanel('');
          return;
        }

        this.dataset.selectedVariantId = String(variantId);
        this.#showPanel(String(variantId));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          this.#setStatus('تعذّر تحديث الإضافات. حاول اختيار النسخة مرة أخرى.');
        }
      })
      .finally(() => this.removeAttribute('aria-busy'));
  };

  #showPanel(variantId) {
    let activePanel = null;
    for (const panel of this.querySelectorAll('[data-addon-panel]')) {
      const isActive = panel.dataset.variantId === String(variantId);
      panel.hidden = !isActive;
      if (isActive) activePanel = panel;
    }

    const hasAvailableItems = activePanel?.dataset.hasAvailableAddons === 'true';
    this.hidden = !activePanel || !hasAvailableItems;
    this.#setStatus(hasAvailableItems ? '' : this.dataset.emptyLabel || '');
  }

  #setQuantity(input, requestedQuantity) {
    const min = Number(input.min) || 0;
    const inputMax = input.hasAttribute('max') ? Number(input.max) : Number.NaN;
    const datasetMax = this.dataset.maxQuantity ? Number(this.dataset.maxQuantity) : Number.NaN;
    const max = Number.isFinite(inputMax) ? inputMax : (Number.isFinite(datasetMax) ? datasetMax : Number.POSITIVE_INFINITY);
    const quantity = Math.max(min, Math.min(max, Number.isFinite(requestedQuantity) ? requestedQuantity : 0));
    input.value = String(Math.trunc(quantity));
    input.setAttribute('value', input.value);
    input.closest('[data-addon-item]')?.setAttribute('data-selected', quantity > 0 ? 'true' : 'false');
    this.#syncQuantityControls(input);
  }

  #syncQuantityControls(input) {
    const item = input.closest('[data-addon-item]');
    const decrease = item?.querySelector('[data-addon-decrease]');
    const increase = item?.querySelector('[data-addon-increase]');
    const quantity = Number(input.value) || 0;
    if (decrease) decrease.disabled = input.disabled || quantity <= Number(input.min || 0);
    const inputMax = input.hasAttribute('max') ? Number(input.max) : Number.NaN;
    const datasetMax = this.dataset.maxQuantity ? Number(this.dataset.maxQuantity) : Number.NaN;
    const max = Number.isFinite(inputMax) ? inputMax : (Number.isFinite(datasetMax) ? datasetMax : Number.POSITIVE_INFINITY);
    if (increase) increase.disabled = input.disabled || quantity >= max;
  }

  #setStatus(message) {
    const status = this.querySelector('[data-addon-status]');
    if (status) status.textContent = message;
  }
}

if (!customElements.get('piltron-bundle-addons')) {
  customElements.define('piltron-bundle-addons', PiltronBundleAddons);
}
